import dayjs from "dayjs"
import { useMemo } from "react"

import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { GoodsReceipt, SignedUrl } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { PURCHASE_ORDER_DOCUMENT_TYPE_TO_LABEL } from "@/screens/Procurement/PurchaseOrder/constants"

const PURCHASE_ORDER_INHERITED_DOCUMENT_TYPES = Array.from(PURCHASE_ORDER_DOCUMENT_TYPE_TO_LABEL.keys()).filter(
  (documentType) => documentType !== FieldGroupEnum.PurchaseOrderVendorInvoice
)

interface UseGoodsReceiptDocumentsParams {
  goodsReceipt: GoodsReceipt
}

interface UseGoodsReceiptDocumentsReturn {
  documents: SignedUrl[]
  isFetching: boolean
  refetch: (immediate?: boolean) => void
}

export const useGoodsReceiptDocuments = ({
  goodsReceipt,
}: UseGoodsReceiptDocumentsParams): UseGoodsReceiptDocumentsReturn => {
  const purchaseOrderIds = useMemo(
    () => goodsReceipt.orders?.map(({ orderId }) => String(orderId)) ?? [],
    [goodsReceipt.orders]
  )

  const {
    fileList: goodsReceiptDocuments,
    fileFetching: isGoodsReceiptDocumentsFetching,
    refetchFileUploadData: refetchGoodsReceiptDocuments,
  } = useGetFileUploadData({
    referenceId: goodsReceipt.goodsReceiptId,
    referenceTableName: ReferenceTableName.AdvancedGoodsReceipt,
  })

  const {
    fileList: purchaseOrderDocuments,
    fileFetching: isPurchaseOrderDocumentsFetching,
    refetchFileUploadData: refetchPurchaseOrderDocuments,
  } = useGetFileUploadData({
    referenceIds: purchaseOrderIds,
    fieldGroups: PURCHASE_ORDER_INHERITED_DOCUMENT_TYPES,
    referenceTableName: ReferenceTableName.Order,
    pause: !purchaseOrderIds.length,
  })

  const documents = useMemo(
    () =>
      [...goodsReceiptDocuments, ...purchaseOrderDocuments].sort(
        (a, b) => dayjs(b.file?.createdOn).valueOf() - dayjs(a.file?.createdOn).valueOf()
      ),
    [goodsReceiptDocuments, purchaseOrderDocuments]
  )

  const refetch = (immediate?: boolean): void => {
    refetchGoodsReceiptDocuments(immediate)
    if (purchaseOrderIds.length) refetchPurchaseOrderDocuments(immediate)
  }

  return {
    documents,
    isFetching: isGoodsReceiptDocumentsFetching || isPurchaseOrderDocumentsFetching,
    refetch,
  }
}
